import { useEffect, useRef, type RefObject } from 'react';
import { useUIStore } from '../store/uiStore';
import { useEscapeToClose } from '../utils/escapeStack';
import {
  parsePlanDropAttr,
  planDropAttr,
  type PlanDragSource,
  type PlanDropOutcome,
  type PlanDropTarget,
} from '../utils/planListDrop';

// Drag a defense row in the plan list onto a bundle card, out of one, or onto
// another loose row (Oct 2026). A mouse shortcut on top of the row's ⋯ menu,
// which stays the keyboard path. One delegated native pointerdown listener on
// the list container; rows carry data-plan-drag-id, drop targets carry
// data-plan-drop, and anything inside [data-plan-nodrag] never starts a drag.
// The ghost's position is written straight to its DOM node each frame; only
// the target and outcome go through the store, and only when they change.

/** Pixels the pointer must travel from pointerdown before a drag begins. */
export const PLAN_DRAG_THRESHOLD = 6;
const EDGE_ZONE = 40;
const MAX_SCROLL_STEP = 12;
const GHOST_OFFSET = 12;

export interface PlanListDragOptions {
  /** The feature list's container (a react-native-web View ref is its DOM node). */
  listRef: RefObject<unknown>;
  /** The panel's ScrollView (or a DOM element): auto-scrolls at its edges. */
  scrollRef: RefObject<unknown>;
  /** The drag ghost's node (PlanDragGhost); moved with a transform. */
  ghostRef: RefObject<unknown>;
  /** False when the panel isn't editable. */
  enabled: boolean;
  resolve: (drag: PlanDragSource, target: PlanDropTarget | null) => PlanDropOutcome | null;
  onDrop: (outcome: PlanDropOutcome, featureIds: string[]) => void;
}

interface Session {
  pointerId: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
  rowNode: HTMLElement;
  pressedId: string;
  sourceBundleId: string | null;
  started: boolean;
  cancelled: boolean;
  featureIds: string[];
  targetKey: string | null;
  hoveredBundleId: string | null;
  raf: number;
  lastScrollTop: number;
  restore: { userSelect: string; webkitUserSelect: string; cursor: string } | null;
}

function toElement(node: unknown): HTMLElement | null {
  if (!node) return null;
  const maybe = node as { getScrollableNode?: () => unknown };
  const inner = typeof maybe.getScrollableNode === 'function' ? maybe.getScrollableNode() : node;
  const el = inner as HTMLElement;
  return typeof el?.addEventListener === 'function' ? el : null;
}

interface Controller {
  attach: (list: HTMLElement) => () => void;
  cancel: () => void;
  isLive: () => boolean;
}

function createController(getOptions: () => PlanListDragOptions): Controller {
  const session: { current: Session | null } = { current: null };

  function attach(list: HTMLElement): () => void {
    const onPointerDown = (e: PointerEvent) => {
      if (session.current) return;
      if (e.button !== 0) return;
      if (e.pointerType === 'touch') return;
      const from = e.target as Element | null;
      if (!from?.closest) return;
      if (from.closest('[data-plan-nodrag]')) return;
      const row = from.closest('[data-plan-drag-id]') as HTMLElement | null;
      if (!row || !list.contains(row)) return;
      const pressedId = row.getAttribute('data-plan-drag-id');
      if (!pressedId) return;
      const next: Session = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        x: e.clientX,
        y: e.clientY,
        rowNode: row,
        pressedId,
        sourceBundleId: row.getAttribute('data-plan-drag-bundle') || null,
        started: false,
        cancelled: false,
        featureIds: [pressedId],
        targetKey: null,
        hoveredBundleId: null,
        raf: 0,
        lastScrollTop: 0,
        restore: null,
      };
      session.current = next;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onAbort);
      document.addEventListener('lostpointercapture', onAbort);
    };

    list.addEventListener('pointerdown', onPointerDown);
    return () => {
      list.removeEventListener('pointerdown', onPointerDown);
      endSession(false);
    };
  }

  function scrollNode(): HTMLElement | null {
    return toElement(getOptions().scrollRef.current);
  }

  function ownsEvent(e: PointerEvent): boolean {
    const s = session.current;
    return !!s && (e.pointerId === undefined || e.pointerId === s.pointerId);
  }

  function onMove(e: PointerEvent) {
    const s = session.current;
    if (!s || !ownsEvent(e)) return;
    s.x = e.clientX;
    s.y = e.clientY;
    if (!s.started) {
      if (Math.hypot(s.x - s.startX, s.y - s.startY) < PLAN_DRAG_THRESHOLD) return;
      startDrag(s);
    }
    if (s.cancelled) return;
    updateTarget(s);
  }

  function startDrag(s: Session) {
    s.started = true;
    const multi = useUIStore.getState().planMultiSelection;
    s.featureIds =
      multi.length >= 2 && multi.includes(s.pressedId)
        ? [s.pressedId, ...multi.filter((id) => id !== s.pressedId)]
        : [s.pressedId];
    try {
      s.rowNode.setPointerCapture?.(s.pointerId);
    } catch {
      // The pointer is already gone; the document listeners still end the drag.
    }
    const root = document.documentElement;
    const style = root.style as CSSStyleDeclaration & { webkitUserSelect?: string };
    s.restore = {
      userSelect: style.userSelect,
      webkitUserSelect: style.webkitUserSelect ?? '',
      cursor: style.cursor,
    };
    style.userSelect = 'none';
    style.webkitUserSelect = 'none';
    style.cursor = 'grabbing';
    window.getSelection?.()?.removeAllRanges();
    document.addEventListener('selectstart', blockSelect);
    scrollNode()?.addEventListener('scroll', onScroll);
    s.lastScrollTop = scrollNode()?.scrollTop ?? 0;
    useUIStore.getState().setPlanListDrag({
      featureIds: s.featureIds,
      sourceBundleId: s.sourceBundleId,
      target: null,
      outcome: null,
    });
    s.raf = requestAnimationFrame(frame);
  }

  function blockSelect(e: Event) {
    e.preventDefault();
  }

  function onScroll() {
    const s = session.current;
    if (s?.started && !s.cancelled) updateTarget(s);
  }

  function targetAt(x: number, y: number): PlanDropTarget | null {
    if (typeof document.elementsFromPoint !== 'function') return null;
    for (const el of document.elementsFromPoint(x, y)) {
      const hit = (el as Element).closest?.('[data-plan-drop]');
      if (hit) return parsePlanDropAttr(hit.getAttribute('data-plan-drop'));
    }
    return null;
  }

  function computeOutcome(s: Session, target: PlanDropTarget | null) {
    return getOptions().resolve(
      { featureIds: s.featureIds, sourceBundleId: s.sourceBundleId },
      target
    );
  }

  function updateTarget(s: Session) {
    const target = targetAt(s.x, s.y);
    const outcome = computeOutcome(s, target);
    const key = `${target ? planDropAttr(target) : ''}|${outcome?.kind ?? ''}|${outcome?.label ?? ''}`;
    if (key === s.targetKey) return;
    s.targetKey = key;
    const ui = useUIStore.getState();
    ui.setPlanListDragTarget(target, outcome);
    const lit =
      target?.type === 'bundle' && outcome && outcome.kind !== 'none' && !outcome.disabled
        ? target.id
        : null;
    if (lit !== s.hoveredBundleId) {
      if (s.hoveredBundleId) ui.clearHoveredPlanBundle(s.hoveredBundleId);
      if (lit) ui.setHoveredPlanBundle(lit);
      s.hoveredBundleId = lit;
    }
  }

  function frame() {
    const s = session.current;
    if (!s || !s.started) return;
    const ghost = getOptions().ghostRef.current as HTMLElement | null;
    if (ghost?.style) {
      ghost.style.transform = `translate3d(${s.x + GHOST_OFFSET}px, ${s.y + GHOST_OFFSET}px, 0)`;
      ghost.style.opacity = '1';
    }
    const scroller = scrollNode();
    if (scroller && !s.cancelled) {
      const rect = scroller.getBoundingClientRect();
      let step = 0;
      if (s.y < rect.top + EDGE_ZONE) {
        step = -Math.ceil(MAX_SCROLL_STEP * Math.min(1, (rect.top + EDGE_ZONE - s.y) / EDGE_ZONE));
      } else if (s.y > rect.bottom - EDGE_ZONE) {
        step = Math.ceil(
          MAX_SCROLL_STEP * Math.min(1, (s.y - (rect.bottom - EDGE_ZONE)) / EDGE_ZONE)
        );
      }
      // Only while the pointer is still over the scroller's column.
      if (step !== 0 && s.x >= rect.left && s.x <= rect.right) {
        scroller.scrollTop += step;
        if (scroller.scrollTop !== s.lastScrollTop) {
          s.lastScrollTop = scroller.scrollTop;
          updateTarget(s);
        }
      }
    }
    s.raf = requestAnimationFrame(frame);
  }

  /** Puts back everything a started drag changed. The session stays so a
   *  cancelled drag can still swallow the click its release produces. */
  function endVisuals(s: Session) {
    if (s.raf) cancelAnimationFrame(s.raf);
    s.raf = 0;
    document.removeEventListener('selectstart', blockSelect);
    scrollNode()?.removeEventListener('scroll', onScroll);
    if (s.restore) {
      const style = document.documentElement.style as CSSStyleDeclaration & {
        webkitUserSelect?: string;
      };
      style.userSelect = s.restore.userSelect;
      style.webkitUserSelect = s.restore.webkitUserSelect;
      style.cursor = s.restore.cursor;
      s.restore = null;
    }
    try {
      s.rowNode.releasePointerCapture?.(s.pointerId);
    } catch {
      // Capture was already released.
    }
    const ui = useUIStore.getState();
    if (s.hoveredBundleId) ui.clearHoveredPlanBundle(s.hoveredBundleId);
    s.hoveredBundleId = null;
    ui.setPlanListDrag(null);
  }

  /** Removes the document listeners and forgets the session. `swallowClick`
   *  keeps the row from selecting on the click that follows a drag's release. */
  function endSession(swallowClick: boolean) {
    const s = session.current;
    if (!s) return;
    session.current = null;
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onAbort);
    document.removeEventListener('lostpointercapture', onAbort);
    if (s.started) {
      if (!s.cancelled) endVisuals(s);
      if (swallowClick) swallowNextClick();
    }
  }

  function swallowNextClick() {
    const stop = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      clear();
    };
    const clear = () => {
      clearTimeout(timer);
      document.removeEventListener('click', stop, true);
    };
    const timer = setTimeout(clear, 0);
    document.addEventListener('click', stop, true);
  }

  function onUp(e: PointerEvent) {
    const s = session.current;
    if (!s || !ownsEvent(e)) return;
    if (!s.started) {
      endSession(false);
      return;
    }
    if (s.cancelled) {
      endSession(true);
      return;
    }
    s.x = e.clientX;
    s.y = e.clientY;
    const target = targetAt(s.x, s.y);
    const outcome = computeOutcome(s, target);
    const ids = s.featureIds;
    endSession(true);
    if (outcome && outcome.kind !== 'none' && !outcome.disabled) {
      getOptions().onDrop(outcome, ids);
    }
  }

  function onAbort(e: PointerEvent) {
    const s = session.current;
    if (!s || !ownsEvent(e)) return;
    // cancel() releases the capture itself, and the browser answers with
    // lostpointercapture: that is not the pointer going away, so the session
    // stays until pointerup, which then swallows the click.
    if (e.type === 'lostpointercapture' && s.cancelled) return;
    endSession(false);
  }

  function cancel() {
    const s = session.current;
    if (!s || !s.started || s.cancelled) return;
    // The button is usually still down: keep the session until it comes up
    // so that release doesn't read as a click on the row.
    s.cancelled = true;
    endVisuals(s);
  }

  return {
    attach,
    cancel,
    isLive: () => !!session.current?.started && !session.current.cancelled,
  };
}

export function usePlanListDrag(options: PlanListDragOptions): void {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const ctrlRef = useRef<Controller | null>(null);
  if (!ctrlRef.current) ctrlRef.current = createController(() => optionsRef.current);
  const ctrl = ctrlRef.current;

  const pickerOpen = useUIStore((s) => !!s.planBundleSelection);
  const dragging = useUIStore((s) => !!s.planListDrag);
  // An armed draw tool does not disable dragging: the list is not the map,
  // and the tool stays armed after a point commits, which is exactly when
  // several fresh points get bundled (Oct 14 2026 browser check). Arming a
  // tool mid-drag still cancels the drag (setPlanDrawTool clears the slice).
  const active = options.enabled && !pickerOpen;

  const attachedRef = useRef<{ node: HTMLElement; teardown: () => void } | null>(null);

  // Escape cancels through the shared layer stack, so the panel's own Escape
  // handler (close the panel, clear the selection) stands down meanwhile.
  useEscapeToClose(dragging, ctrl.cancel);

  // Re-checked after every render: the list container mounts and unmounts as
  // the plan fills, empties and enters the bundle picker.
  useEffect(() => {
    const node = active ? toElement(options.listRef.current) : null;
    const attached = attachedRef.current;
    if (attached && attached.node === node) return;
    attached?.teardown();
    attachedRef.current = null;
    if (!node) return;
    attachedRef.current = { node, teardown: ctrl.attach(node) };
  });

  useEffect(
    () => () => {
      attachedRef.current?.teardown();
      attachedRef.current = null;
    },
    []
  );

  // The picker opened or a tool armed mid-drag: the store already dropped the
  // slice; drop the session too.
  useEffect(() => {
    if (!dragging && ctrl.isLive()) ctrl.cancel();
  }, [dragging, ctrl]);
}
